<template>
  <div max-w-lg>
    <div class="adthrive-header min-h-[280px] overflow-hidden"></div>
    <Ad mb-4 ad-slot="6966721074" />
    <div pb-5>
      <h2 font-slab text-lg sm:text-xl text-brand-blue>
        <NuxtLink :to="`/county-map/${route.params.state}`" class="hover:text-blue-300"
          >{{ countyData.state.name }} {{ countyData.countyType.one }} Maps</NuxtLink
        >
      </h2>
      <p text-stone-500>
        Find {{ countyData.state.name }} {{ countyData.countyType.one.toLowerCase() }} and township maps.
      </p>
    </div>
    <h1 text-3xl sm:text-4xl font-semibold text-stone-500>{{ countyData.fullName }}</h1>
    <div py-10 space-y-12>
      <p>
        Find your {{ countyData.state.name }} {{ countyData.countyType.one }} by searching for your address, or by
        clicking on the map.
      </p>
      <div space-y-4>
        <h2 text-2xl font-semibold text-stone-500>{{ countyData.fullName }} Map</h2>
        <div w-full h-lg bg-stone-50>
          <LazyMapAddress
            field="countySubdivision.county.name"
            :bbox="countyData.bbox"
            :filter="['==', 'STATEFP', countyData.state.id]"
            :value="countyData.name"
            :options="{ fitBoundsOptions: { padding: 130 } }"
            @click="handleClick"
          />
        </div>
        <p>
          A detailed map of {{ countyData.name }} {{ countyData.countyType.one }}. Click on a nearby
          {{ countyData.countyType.one }} to view more information about that {{ countyData.countyType.one }}.
        </p>
        <p>
          <Button color="blue" to="/what-county-am-i-in" icon="i-material-symbols-my-location-rounded">
            Locate my {{ countyData.state.name }} {{ countyData.countyType.one.toLowerCase() }}
          </Button>
        </p>
      </div>
      <div v-if="countyData.state.countySubdivisions?.length" space-y-4>
        <h2 text-2xl font-semibold text-stone-500>
          Map of {{ countyData.state.countySubdivisions[0].many }} in {{ countyData.name }}
          {{ countyData.countyType.one }},
          {{ countyData.state.name }}
        </h2>
        <div w-full h-lg bg-stone-50>
          <LazyMapAddress
            field="countySubdivision.fullName"
            :bbox="countyData.bbox"
            :filter="['all', ['==', 'STATEFP', countyData.state.id], ['==', 'COUNTYFP', countyData.id.substring(2)]]"
          />
        </div>
        <p>
          An interactive map of {{ countyData.countyType.one.toLowerCase() }} subdivisions in {{ countyData.name }}
          {{ countyData.countyType.one }}. In {{ countyData.state.name }}, that can include
          {{ arrayToTextList(countyData.state.countySubdivisions.map((s) => s.many)) }}.
          <NuxtLink :to="countySubdivisionLink(countyData.state)" class="font-bold hover:text-stone-400"
            >Find what {{ countyData.state.countySubdivisions[0].one.toLowerCase() }} you are in now.</NuxtLink
          >
        </p>
        <p>
          <Button
            color="blue"
            icon="i-material-symbols-my-location-rounded"
            :to="countySubdivisionLink(countyData.state)"
          >
            Locate my {{ countyData.state.name }} {{ countyData.state.countySubdivisions[0].one.toLowerCase() }}
          </Button>
        </p>
      </div>
      <div space-y-4>
        <h2 text-xl sm:text-2xl font-semibold text-stone-500>
          Map of {{ countyData.countyType.many }} in {{ countyData.state.name }}
        </h2>
        <div w-full h-lg bg-stone-50>
          <LazyMapAddress
            field="countySubdivision.county.name"
            :bbox="countyData.state.bbox"
            :filter="['==', 'STATEFP', countyData.state.id]"
            @click="handleClick"
          />
        </div>
        <p>
          An interactive map of {{ countyData.countyType.many }} in {{ countyData.state.name }}. Click on a
          {{ countyData.countyType.one }} to view more information about that {{ countyData.countyType.one }}.
        </p>
      </div>
      <WikipediaInfo v-if="countyData.wiki" :slug="countyData.wiki" />
      <div space-y-4>
        <h2 text-xl sm:text-2xl font-semibold text-stone-500>What is this?</h2>
        <p>
          This site has an interactive map of {{ countyData.state.name }} {{ countyData.countyType.many }}. Click on the
          map to find your {{ countyData.state.name }} county, and view more details. You can also search for your
          address and use your GPS to find your {{ countyData.state.name }} {{ countyData.countyType.one }}.
        </p>
        <p>
          Find what {{ countyData.countyType.one }} you are in, and see official {{ countyData.state.name }}
          {{ countyData.countyType.one }} resources and information. Find any county in the United States, along with
          resources and links to official {{ countyData.countyType.one }} websites. See a
          {{ countyData.countyType.one }} map and local details. Looking for what county you are currently in? Find out
          on this map or
          <NuxtLink to="/what-county-am-i-in" font-semibold hover:text-stone-400
            >use your device GPS to see what county you are in</NuxtLink
          >.
        </p>
      </div>
      <div v-if="countyData.snowPlaces?.length" space-y-4>
        <h2 text-xl sm:text-2xl font-semibold text-stone-500>Snow Totals in {{ countyData.fullName }}</h2>
        <p>See recent snowfall, snow depth, and records for each city and town in {{ countyData.fullName }}.</p>
        <ul flex flex-wrap gap-x-5 gap-y-2>
          <li v-for="place in countyData.snowPlaces" :key="place.url">
            <NuxtLink :to="place.url" font-semibold class="hover:text-stone-400">{{ place.name }}</NuxtLink>
          </li>
        </ul>
      </div>
      <MoreResourcesLinks
        v-if="countyData?.urlTitle"
        :center="countyData.center"
        :urlName="countyData.urlTitle"
        :displayName="countyData.fullName"
      />
    </div>
  </div>
</template>
<script setup>
const route = useRoute();
const router = useRouter();

function handleClick(features) {
  const county = features[0]?.properties?.NAME;
  if (county) {
    router.push(`/county-map/${route.params.state}/${encodeUrlSlug(county)}`);
  }
}
const { data: countyData } = await useFetch("/api/county", {
  query: { state: route.params.state, county: route.params.county },
});

useSeoMeta({
  title: () => `${countyData.value.fullName} Map - What ${countyData.value.countyType.one.toLowerCase()} am I in?`,
  description: () =>
    `View a map of ${countyData.value.fullName}, find what ${countyData.value.state.name} ${countyData.value.countyType.one} you are in, and see official ${countyData.value.fullName} resources and information.`,
});

// const imageSubtitle = computed(() => {
//   let subtitle = `Maps of ${countyData.value.state.name} by ${countyData.value.countyType.one.toLowerCase()}`;
//   if (countyData.value.state?.countySubdivisions) {
//     subtitle += ` and ${countyData.value.state.countySubdivisions[0].one.toLowerCase()}`;
//   }
//   return subtitle;
// });

// defineOgImage({
//   title: countyData.value.fullName,
//   subtitle: imageSubtitle.value,
//   cacheMaxAgeSeconds: 0,
//   runtimeCacheStorage: false,
// });
</script>
